import { mapGuaranteesTenderResponseToDetails } from '~/modules/guarantees/form/client/tenderMapper.ts';
import type {
  TGuaranteesTenderDetails,
  TGuaranteesTenderSearchResponse,
} from '~/modules/guarantees/form/client/types.ts';
import type { TGuaranteesApplicationTenderInfo } from '~/store/guarantees/types.ts';

const getRecordValue = (value: unknown, key: string): unknown =>
  value && typeof value === 'object' ? (value as Record<string, unknown>)[key] : undefined;

export const parseGuaranteesTenderSearchResponse = (
  rawFeedJson?: string | null
): TGuaranteesTenderSearchResponse | null => {
  if (!rawFeedJson?.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawFeedJson);

    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    const data = getRecordValue(parsed, 'data');
    const config = getRecordValue(parsed, 'config');

    return {
      data: (data && typeof data === 'object'
        ? data
        : parsed) as TGuaranteesTenderSearchResponse['data'],
      config:
        config && typeof config === 'object'
          ? (config as TGuaranteesTenderSearchResponse['config'])
          : null,
    };
  } catch {
    return null;
  }
};

export const parseGuaranteesTenderDetailsFromRawFeedJson = (
  rawFeedJson?: string | null
): TGuaranteesTenderDetails | null => {
  const response = parseGuaranteesTenderSearchResponse(rawFeedJson);

  return response ? mapGuaranteesTenderResponseToDetails(response) : null;
};

export const hasGuaranteesTenderRawLots = (rawFeedJson?: string | null): boolean => {
  const response = parseGuaranteesTenderSearchResponse(rawFeedJson);

  return Array.isArray(response?.data?.lots) && response.data.lots.length > 0;
};

export const getGuaranteesSelectedTenderLotIds = (
  tenderInfo?: TGuaranteesApplicationTenderInfo | null
): string[] => {
  const tenderLotIds =
    tenderInfo?.tender?.lots
      ?.map((lot) => String(lot?.id ?? '').trim())
      .filter(Boolean) ?? [];

  if (tenderLotIds.length) {
    return tenderLotIds;
  }

  return tenderInfo?.lotIds?.map((lotId) => String(lotId ?? '').trim()).filter(Boolean) ?? [];
};

export const areGuaranteesTenderLotIdsEqual = (
  firstLotIds: string[],
  secondLotIds: string[]
): boolean => {
  const normalizedFirstLotIds = firstLotIds.map((lotId) => lotId.trim()).filter(Boolean);
  const normalizedSecondLotIds = secondLotIds.map((lotId) => lotId.trim()).filter(Boolean);

  return (
    normalizedFirstLotIds.length === normalizedSecondLotIds.length &&
    normalizedFirstLotIds.every((lotId, index) => lotId === normalizedSecondLotIds[index])
  );
};
